import React from "react";
import {
  TextField,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { UseFormRegisterReturn } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";
import { textFieldSx as baseTextFieldSx } from "./formStyles";

export interface FormTextFieldProps {
  // react-hook-form mode (pass register result)
  name?: string;
  register?: UseFormRegisterReturn;
  error?: boolean;
  helperText?: string;
  // standalone mode
  value?: string;
  onChange?: (value: string) => void;
  // common
  placeholder?: string;
  // "password" | "tel" added additively (settings-v2 SInputField adapter,
  // SETTINGS_V2_SP0); "time" added additively (SETTINGS_V2_SP8 — Služba
  // business-hours inputs). MUI TextField forwards any string to <input type>,
  // so these are safe; existing callers are unaffected.
  type?: "text" | "number" | "date" | "password" | "tel" | "time";
  fullWidth?: boolean;
  maxLength?: number;
  disabled?: boolean;
  inputProps?: Record<string, unknown>;
  size?: "small" | "medium";
  hintText?: string;
  sx?: SxProps<Theme>;
  // ── Additive (SETTINGS_V2_SP2 §8.1 — co-owned shared lib, logged in
  //    BREAKING_CHANGES.md). Both default OFF, so existing callers are
  //    byte-identical.
  /** Visual mode: "dt" = existing dt-token styling (default); "cssVars" =
   *  inherit the ambient scoped CSS variables (settings-v2 / future v2). */
  visual?: "dt" | "cssVars";
  /** Optional end adornment (e.g. password visibility toggle). Wrapped in MUI
   *  InputAdornment internally; works in register and standalone modes. */
  endAdornment?: React.ReactNode;
}

/**
 * cssVars visual mode — defers every color/font to the ambient scoped CSS
 * custom properties (they resolve because the input renders inside
 * `.settings-v2-root`). SP0 §8 target look. (SETTINGS_V2_SP2 §8.1)
 */
const cssVarsTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "inherit",
    fontSize: "13.5px",
    borderRadius: "8px",
    backgroundColor: "var(--bg-soft)",
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border)" },
    "&:hover fieldset": { borderColor: "var(--mod-accent)" },
    "&.Mui-focused fieldset": { borderColor: "var(--mod-accent)", borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px var(--mod-accent-soft)" },
    "&.Mui-error fieldset": { borderColor: "var(--red)" },
    "&.Mui-disabled": { backgroundColor: "var(--card-2)", cursor: "not-allowed" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "var(--muted)", cursor: "not-allowed" },
    "& .MuiOutlinedInput-input::placeholder": { color: "var(--faint)", opacity: 1 },
  },
};

/**
 * Styled MUI TextField with dt tokens.
 *
 * Supports both react-hook-form mode (via `register`) and standalone mode
 * (via `value` + `onChange`). Based on WO textFieldSx + SR/RFS focus styling.
 */
const FormTextField: React.FC<FormTextFieldProps> = ({
  name,
  register,
  error,
  helperText,
  value,
  onChange,
  placeholder,
  type = "text",
  fullWidth = true,
  maxLength,
  disabled,
  inputProps,
  size = "small",
  hintText,
  sx,
  visual = "dt",
  endAdornment,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // Determine helper text: error text takes priority, then hint text
  const displayHelperText = helperText || (hintText && !error ? hintText : undefined);

  // Build inputProps with maxLength if specified
  const mergedInputProps: Record<string, unknown> = {
    ...(inputProps || {}),
    ...(maxLength ? { maxLength } : {}),
  };

  // Visual mode selects the base sx block (§8.1). "dt" keeps every existing
  // caller pixel-identical; "cssVars" defers to the ambient scoped tokens.
  const resolvedBaseSx = visual === "cssVars" ? cssVarsTextFieldSx : baseTextFieldSx;

  // MUI Input-component props (distinct from the native `inputProps` above).
  const muiInputProps = endAdornment
    ? { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> }
    : undefined;

  // For type="date", force shrink on InputLabel
  const inputLabelProps = type === "date" ? { shrink: true } : undefined;

  // Hint text styling
  const helperTextSx =
    !error && hintText && !helperText
      ? {
          fontStyle: "italic",
          color: dt.textMuted,
          fontFamily: dt.font,
        }
      : { fontFamily: dt.font };

  // react-hook-form mode
  if (register) {
    return (
      <TextField
        {...register}
        fullWidth={fullWidth}
        size={size}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        error={error}
        helperText={displayHelperText}
        inputProps={mergedInputProps}
        InputProps={muiInputProps}
        InputLabelProps={inputLabelProps}
        FormHelperTextProps={{ sx: helperTextSx }}
        sx={{
          ...(resolvedBaseSx as Record<string, unknown>),
          ...((sx as Record<string, unknown>) || {}),
        }}
      />
    );
  }

  // standalone mode
  return (
    <TextField
      name={name}
      fullWidth={fullWidth}
      size={size}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      error={error}
      helperText={displayHelperText}
      inputProps={mergedInputProps}
      InputProps={muiInputProps}
      InputLabelProps={inputLabelProps}
      FormHelperTextProps={{ sx: helperTextSx }}
      sx={{
        ...(resolvedBaseSx as Record<string, unknown>),
        ...((sx as Record<string, unknown>) || {}),
      }}
    />
  );
};

export default FormTextField;
